import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NetworkAuth.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

function NetworkAuth() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [userType, setUserType] = useState(''); // 'brand' or 'influencer'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    fullName: '',
    instagramHandle: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotStatus, setForgotStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isLogin) {
        // Login
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Login failed');
        }

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Redirect based on user type
        if (data.user.userType === 'agent') {
          navigate('/network/agent');
        } else if (data.user.userType === 'brand') {
          navigate('/network/brand-dashboard');
        } else {
          navigate('/network/influencer-dashboard');
        }
      } else {
        // Registration
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        if (!userType) {
          throw new Error('Please select an account type');
        }

        const registrationData = {
          email: formData.email,
          password: formData.password,
          userType,
          ...(userType === 'brand' 
            ? { companyName: formData.companyName }
            : {
                fullName: formData.fullName,
                instagramHandle: (formData.instagramHandle || '').replace(/^@/, ''),
              }
          ),
        };

        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(registrationData),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Registration failed');
        }

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Send fresh registrations to a profile-completion landing so they
        // prioritize connecting socials / completing their profile first.
        if (data.user.userType === 'brand') {
          navigate('/network/brand-dashboard?tab=profile&welcome=1');
        } else {
          navigate('/network/influencer-profile?welcome=1');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setForgotStatus('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Request failed');
      setForgotStatus(data.message || 'If an account exists for that email, a reset link has been sent.');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openForgot = () => {
    setIsForgot(true);
    setError('');
    setForgotStatus('');
  };

  const backToLogin = () => {
    setIsForgot(false);
    setError('');
    setForgotStatus('');
    setIsLogin(true);
  };

  return (
    <div className="network-auth-page">
      {/* Background */}
      <div className="auth-bg">
        <div className="auth-gradient"></div>
        <div className="auth-particles"></div>
      </div>

      {/* Content */}
      <div className="auth-container">
        <div className="auth-header">
          <img src="/network-logo.png" alt="Alooma Network" className="network-auth-logo" />
          <h1 className="auth-title">
            Alooma <span className="accent">Network</span>
          </h1>
          <p className="auth-subtitle">
            {isLogin 
              ? 'Sign in to access your dashboard'
              : 'Join the premier creator-brand marketplace'
            }
          </p>
        </div>

        {/* Toggle */}
        {!isForgot && (
        <div className="auth-toggle">
          <button
            className={`toggle-btn ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
          >
            Sign In
          </button>
          <button
            className={`toggle-btn ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
          >
            Register
          </button>
        </div>
        )}

        {/* User Type Selection (Registration only) */}
        {!isForgot && !isLogin && !userType && (
          <div className="user-type-selection">
            <h2>I am a...</h2>
            <div className="type-cards">
              <button
                className="type-card"
                onClick={() => setUserType('brand')}
              >
                <div className="type-icon">🏢</div>
                <h3>Brand</h3>
                <p>Post campaigns and connect with top creators</p>
              </button>
              <button
                className="type-card"
                onClick={() => setUserType('influencer')}
              >
                <div className="type-icon">⭐</div>
                <h3>Creator</h3>
                <p>Discover opportunities and grow your career</p>
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {!isForgot && (isLogin || userType) && (
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}

            {/* Registration: Show selected type */}
            {!isLogin && userType && (
              <div className="selected-type">
                <span>Registering as: <strong>{userType === 'brand' ? 'Brand' : 'Creator'}</strong></span>
                <button type="button" onClick={() => setUserType('')}>Change</button>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="••••••••"
                minLength={6}
              />
            </div>

            {/* Registration fields */}
            {!isLogin && (
              <>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                  />
                </div>

                {userType === 'brand' && (
                  <div className="form-group">
                    <label htmlFor="companyName">Company Name</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder="Your company name"
                    />
                  </div>
                )}

                {userType === 'influencer' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="instagramHandle">Instagram Handle <span className="optional-hint">(optional)</span></label>
                      <input
                        type="text"
                        id="instagramHandle"
                        name="instagramHandle"
                        value={formData.instagramHandle}
                        onChange={handleChange}
                        placeholder="@yourhandle"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? (
                <span className="loading">Processing...</span>
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>

            {isLogin && (
              <div className="form-footer">
                <button type="button" className="forgot-link" onClick={openForgot}>
                  Forgot password?
                </button>
              </div>
            )}
          </form>
        )}

        {/* Forgot Password Form */}
        {isForgot && (
          <form onSubmit={handleForgotSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}
            {forgotStatus ? (
              <div className="forgot-success">
                <p>{forgotStatus}</p>
                <button type="button" className="submit-btn" onClick={backToLogin}>
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <p className="forgot-intro">
                  Enter your account email and we'll send you a link to reset your password.
                </p>
                <div className="form-group">
                  <label htmlFor="forgot-email">Email</label>
                  <input
                    type="email"
                    id="forgot-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                  />
                </div>
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <div className="form-footer">
                  <button type="button" className="forgot-link" onClick={backToLogin}>
                    Back to Sign In
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {/* Back to main site */}
        <div className="back-link">
          <a href="/">← Back to Alooma</a>
        </div>
        <div className="auth-legal-links">
          <a href="/terms">Terms of Service</a>
          <span>·</span>
          <a href="/privacy">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}

export default NetworkAuth;
